/**
 * Material Taxonomy Redux Slice
 * Manages state for Material Taxonomy (Packaging PCF) module
 * Reference: Special Ingredients slice pattern
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  IFunctionalUnit,
  IConfiguration,
} from '../modules/packaging-pcf/material-taxonomies/material-detail/types'

interface IMaterialTaxonomyState {
  functionalUnit: IFunctionalUnit
  loading: boolean
  error: string
  configuration: IConfiguration
}

const initialState: IMaterialTaxonomyState = {
  configuration: {
    path: {
      classId: 0,
      classState: 0,
      class: '',
      classCode: null,
      categoryId: 0,
      categoryState: 0,
      category: '',
      categoryCode: null,
    },
    transportCondition: [],
    processingActivities: [],
  },
  functionalUnit: {
    alias: '',
    id: 0,
    name: '',
    code: null,
    state: 0,
    classId: 0,
    categoryId: 0,
    transportCondition: null,
    disposalIncinerationProcessingActivityId: null,
    disposalLandfillProcessingActivityId: null,
    functionalUnitProxy: null,
    isVerified: false,
    verificationComments: null,
    verifiedByName: null,
    verifiedByEmail: null,
    verifiedOn: null,
    created: '',
    updated: null,
    userEmail: null,
    lastUpdatedName: null,
    lastPublishedDate: null,
    researcherName: null,
    researcherEmail: null,
  },
  loading: false,
  error: '',
}

const materialTaxonomySlice = createSlice({
  name: 'materialTaxonomy',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload
    },
    setFunctionalUnit(state, action: PayloadAction<IFunctionalUnit>) {
      state.functionalUnit = {
        ...action.payload,
        disposalIncinerationProcessingActivityId:
          action.payload.disposalIncinerationProcessingActivityId ?? null,
        disposalLandfillProcessingActivityId:
          action.payload.disposalLandfillProcessingActivityId ?? null,
      }
      state.loading = false
      state.error = ''
    },
    setConfiguration(state, action: PayloadAction<IConfiguration>) {
      // Ensure path is never null - use initial state as fallback
      state.configuration = {
        ...action.payload,
        path: action.payload.path || initialState.configuration.path,
        processingActivities: action.payload.processingActivities ?? [],
      }
    },
    setError(state, action: PayloadAction<string>) {
      state.error = action.payload
      state.loading = false
    },
    handlePublishStatus(state, action: PayloadAction<number>) {
      state.functionalUnit.state = action.payload
      if (action.payload === 1) {
        state.functionalUnit.lastPublishedDate = new Date().toISOString()
      }
    },
    handleVerificationStatus(
      state,
      action: PayloadAction<{
        isVerified: boolean
        verificationComments?: string
        verifiedByName: string
        verifiedByEmail: string
        verifiedOn: string
      }>
    ) {
      state.functionalUnit.isVerified = action.payload.isVerified
      state.functionalUnit.verificationComments = action.payload.verificationComments || null
      state.functionalUnit.verifiedByName = action.payload.verifiedByName
      state.functionalUnit.verifiedByEmail = action.payload.verifiedByEmail
      state.functionalUnit.verifiedOn = action.payload.verifiedOn
    },
    setDefaultMaterialTaxonomy: () => initialState,
  },
})

export const {
  setLoading,
  setFunctionalUnit,
  setConfiguration,
  setError,
  handlePublishStatus,
  handleVerificationStatus,
  setDefaultMaterialTaxonomy,
} = materialTaxonomySlice.actions

export default materialTaxonomySlice.reducer
